import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { api, type KeyFields, type KeySummary, type Me, type NewKey, type OwnedServiceKey } from "../api";
import { KeyForm, parseTags, RESET_LABEL } from "../KeyForm";
import { CopyButton, Dialog, Empty, ErrorMessage, Loading, TagChips } from "../components";
import { money, when } from "../format";
import { useForgeEvent } from "../hooks";
import { secretMemory } from "../secretMemory";
import { businessUnitName, divisionName } from "../DivisionPicker";
import { OnboardingDialog } from "../OnboardingDialog";

export const shortAlias = (k: KeySummary) => (k.alias ?? k.id.slice(0, 8)).replace(/^[^:]*:/, "").replace(/~[a-z0-9]+$/, "");

export function KeysPage({ me }: { me: Me }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const keys = useQuery({ queryKey: ["keys"], queryFn: api.keys });
  // Service keys this person owns or backs up (TAI-2568). Someone with none sees the page as before.
  const owned = useQuery({ queryKey: ["my-service-keys"], queryFn: api.myServiceKeys });
  const hasOwned = !!owned.data?.length;
  const [creating, setCreating] = useState(false);
  const [onboarding, setOnboarding] = useState(false);
  /** Create a key; ask for division and business unit first if they are not on record. */
  const startCreate = () => (me.directory ? setCreating(true) : setOnboarding(true));
  const [revealed, setRevealed] = useState<NewKey | null>(null);
  const [revoking, setRevoking] = useState<KeySummary | null>(null);
  const [editing, setEditing] = useState<KeySummary | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (params.get("create") === "1" && !me.readOnly) startCreate();
    if (params.has("create")) setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, setParams]);

  const reveal = (k: NewKey) => {
    secretMemory.set(k.key, k.summary.alias);
    setRevealed(k);
    qc.invalidateQueries({ queryKey: ["keys"] });
  };
  const rotate = useMutation({ mutationFn: (id: string) => api.rotateKey(id), onSuccess: reveal });
  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeKey(id),
    onSuccess: () => {
      setRevoking(null);
      setNotice("Key revoked. It can take up to five minutes to stop working everywhere.");
      qc.invalidateQueries({ queryKey: ["keys"] });
    },
  });

  const canCreate = !me.readOnly;
  const labels = me.directory
    ? `${divisionName(me.taxonomy, me.directory.division)} · ${businessUnitName(me.taxonomy, me.directory.businessUnit)}`
    : null;

  return (
    <>
      <div className="row spread wrap gap-small">
        <div className="stack gap-xsmall grow">
          <p className="forge-typography--body2 muted card-title">
            One key per machine or tool, so a lost laptop means revoking one key. A key is shown once, when it is made.
          </p>
          {labels && (
            <p className="forge-typography--body1 muted card-title">
              New keys are labelled <b>{labels}</b>. Wrong? Ask an administrator.
            </p>
          )}
        </div>
        <forge-button variant="raised" disabled={!canCreate || undefined} onClick={startCreate}>
          <forge-icon slot="start" name="add" /> Create a key
        </forge-button>
      </div>
      {!me.directory && (
        <forge-inline-message theme="warning">
          <forge-icon slot="icon" name="warning" />
          <div slot="title">Your division and business unit are not recorded yet</div>
          <span className="row wrap gap-small">
            Keys need them, so spend is reported to the right place.
            <forge-button dense variant="outlined" onClick={() => setOnboarding(true)}>Add them now</forge-button>
          </span>
        </forge-inline-message>
      )}
      {notice && (
        <forge-inline-message theme="success">
          <forge-icon slot="icon" name="check_circle" />
          <div slot="title">{notice}</div>
        </forge-inline-message>
      )}
      {(rotate.error || revoke.error) && <ErrorMessage error={rotate.error ?? revoke.error} />}

      {hasOwned && <h2 className="forge-typography--heading3 card-title">Personal keys</h2>}
      <forge-card>
        {keys.isPending ? <Loading /> : keys.error ? <ErrorMessage error={keys.error} /> : keys.data.length === 0 ? (
          <Empty title="No keys yet" message="Create one to connect a tool to the gateway." />
        ) : (
          <div className="table-wrap">
            <table className="forge-data-table">
              <thead>
                <tr>
                  <th>Name</th><th>Tags</th><th>Models</th><th className="num">Spend (est.)</th><th className="num">Budget</th>
                  <th>Created</th><th>Last used</th><th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {keys.data.map((k) => (
                  <tr key={k.id}>
                    <td>
                      <div>{shortAlias(k)}</div>
                      <div className="mono muted">…{k.id.slice(-8)}</div>
                    </td>
                    <td><span className="row gap-xsmall"><TagChips division={k.division} businessUnit={k.businessUnit} />
                      {fieldsOf(k).tags.map((t) => <span key={t} className="tag-pill">{t}</span>)}</span></td>
                    <td title={k.models.join(", ")}>{k.models.length ? (k.models.length > 2 ? `${k.models.slice(0, 2).join(", ")} +${k.models.length - 2}` : k.models.join(", ")) : <span className="muted">All models</span>}</td>
                    <td className="num">{money(k.spend)}</td>
                    <td className="num">{k.maxBudget === null ? <span className="muted">none</span>
                      : `${money(k.maxBudget)}${k.budgetDuration ? ` ${RESET_LABEL[k.budgetDuration] ?? k.budgetDuration}` : ""}`}</td>
                    <td className="nowrap">{when(k.createdAt)}</td>
                    <td className="nowrap">{when(k.lastActive)}</td>
                    <td className="nowrap">
                      <forge-icon-button aria-label={`Requests for ${shortAlias(k)}`} onClick={() => navigate(`/logs?key=${k.id}`)}>
                        <forge-icon name="receipt_long" />
                      </forge-icon-button>
                      <forge-tooltip>This key&apos;s requests</forge-tooltip>
                      <forge-icon-button aria-label={`Edit ${shortAlias(k)}`} disabled={me.readOnly || undefined} onClick={() => setEditing(k)}>
                        <forge-icon name="settings" />
                      </forge-icon-button>
                      <forge-tooltip>Models, budget and tags</forge-tooltip>
                      <forge-icon-button aria-label={`Rotate ${shortAlias(k)}`} disabled={me.readOnly || rotate.isPending || undefined}
                        onClick={() => rotate.mutate(k.id)}>
                        <forge-icon name="autorenew" />
                      </forge-icon-button>
                      <forge-tooltip>Replace with a new key, with current labels</forge-tooltip>
                      <forge-icon-button aria-label={`Revoke ${shortAlias(k)}`} disabled={me.readOnly || undefined}
                        onClick={() => setRevoking(k)}>
                        <forge-icon name="delete_outline" />
                      </forge-icon-button>
                      <forge-tooltip>Revoke</forge-tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </forge-card>

      {hasOwned && <OwnedServiceKeys me={me} keys={owned.data!} />}

      <OnboardingDialog me={me} open={onboarding} onClose={() => setOnboarding(false)}
        onSaved={() => { setOnboarding(false); setCreating(true); }} />
      {editing && <EditKeyDialog key={editing.id} me={me} target={editing} onClose={() => setEditing(null)} />}
      <CreateKeyDialog me={me} open={creating} onClose={() => setCreating(false)} onCreated={(k) => { setCreating(false); reveal(k); }} />
      <RevealDialog created={revealed} onClose={() => setRevealed(null)} />
      <Dialog open={!!revoking} onClose={() => setRevoking(null)} label="Revoke key">
        <h2 className="forge-typography--heading3 card-title">Revoke “{revoking && shortAlias(revoking)}”?</h2>
        <p className="forge-typography--body2">
          Any tool using it stops working within about five minutes. This cannot be undone; you can create a new key.
        </p>
        <div className="row gap-small">
          <forge-button variant="raised" theme="error" disabled={revoke.isPending || undefined}
            onClick={() => revoking && revoke.mutate(revoking.id)}>Revoke</forge-button>
          <forge-button onClick={() => setRevoking(null)}>Cancel</forge-button>
        </div>
      </Dialog>
    </>
  );
}

type FormFields = KeyFields & { tagText: string };
const emptyFields = (): FormFields => ({ models: [], maxBudget: null, budgetDuration: null, tags: [], tagText: "" });
const fieldsOf = (k: KeySummary): FormFields => {
  const extra = k.tags.filter((t) => !t.startsWith("division: ") && !t.startsWith("business-unit: "));
  return { models: k.models, maxBudget: k.maxBudget, budgetDuration: (k.budgetDuration as KeyFields["budgetDuration"]) ?? null, tags: extra, tagText: extra.join(", ") };
};

function CreateKeyDialog({ me, open, onClose, onCreated }: { me: Me; open: boolean; onClose: () => void; onCreated: (k: NewKey) => void }) {
  const [name, setName] = useState("");
  const [fields, setFields] = useState(emptyFields);
  const create = useMutation({
    mutationFn: () => api.createKey(name, fields),
    onSuccess: (k) => { setName(""); setFields(emptyFields()); onCreated(k); },
  });
  const tagError = parseTags(fields.tagText).error;
  return (
    <Dialog open={open} onClose={onClose} label="Create a key">
      <h2 className="forge-typography--heading3 card-title">Create a key</h2>
      <forge-text-field label-position="block-start">
        <label htmlFor="key-name">Name</label>
        <input id="key-name" value={name} maxLength={40} placeholder="e.g. work-laptop or claude-code"
          onChange={(e) => setName(e.target.value)} />
        <span slot="support-text">Where it will be used. Letters, digits and dashes.</span>
      </forge-text-field>
      {open && <KeyForm me={me} value={fields} onChange={setFields} />}
      {create.error && <ErrorMessage error={create.error} />}
      <div className="row gap-small">
        <forge-button variant="raised" disabled={!name.trim() || !!tagError || create.isPending || undefined} onClick={() => create.mutate()}>
          Create
        </forge-button>
        <forge-button onClick={onClose}>Cancel</forge-button>
      </div>
    </Dialog>
  );
}

function EditKeyDialog({ me, target, onClose }: { me: Me; target: KeySummary | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [fields, setFields] = useState(() => (target ? fieldsOf(target) : emptyFields()));
  const save = useMutation({
    mutationFn: () => api.updateKey(target!.id, fields),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["keys"] }); onClose(); },
  });
  const tagError = parseTags(fields.tagText).error;
  return (
    <Dialog open={!!target} onClose={onClose} label="Edit key">
      <h2 className="forge-typography--heading3 card-title">Edit “{target && shortAlias(target)}”</h2>
      {target && <KeyForm me={me} value={fields} onChange={setFields} />}
      <p className="forge-typography--body1 muted">Changes take effect within a few minutes; the key itself does not change.</p>
      {save.error && <ErrorMessage error={save.error} />}
      <div className="row gap-small">
        <forge-button variant="raised" disabled={!!tagError || save.isPending || undefined} onClick={() => save.mutate()}>Save</forge-button>
        <forge-button onClick={onClose}>Cancel</forge-button>
      </div>
    </Dialog>
  );
}

function RevealDialog({ created, onClose }: { created: NewKey | null; onClose: () => void }) {
  const navigate = useNavigate();
  return (
    <Dialog open={!!created} onClose={onClose} label="Your new key" persistent>
      <h2 className="forge-typography--heading3 card-title">Copy your key now</h2>
      <forge-inline-message theme="warning">
        <forge-icon slot="icon" name="warning" />
        <div slot="title">This is the only time it is shown</div>
        <span>It is not stored anywhere we can show you again. If you lose it, rotate or create another.</span>
      </forge-inline-message>
      {created && (
        <div className="secret-box">
          <code className="mono grow">{created.key}</code>
          <CopyButton text={created.key} />
        </div>
      )}
      <div className="row gap-small">
        <forge-button variant="raised" onClick={() => { onClose(); navigate("/setup"); }}>
          <forge-icon slot="start" name="code" /> Set up a tool with it
        </forge-button>
        <forge-button onClick={() => { onClose(); }}>Done</forge-button>
      </div>
    </Dialog>
  );
}

/**
 * Service keys this person owns or backs up (key policy §4.3 and §4.6, TAI-2568). The owner and the
 * backup owner have equal rights here: see, rotate and revoke. Owners, team, models and budget stay
 * with administrators, so there is no edit.
 */
function OwnedServiceKeys({ me, keys }: { me: Me; keys: OwnedServiceKey[] }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [rotating, setRotating] = useState<OwnedServiceKey | null>(null);
  const [revoking, setRevoking] = useState<OwnedServiceKey | null>(null);
  const [created, setCreated] = useState<{ key: string; summary: OwnedServiceKey } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const rotate = useMutation({
    mutationFn: (id: string) => api.rotateMyServiceKey(id),
    onSuccess: async (k) => { setRotating(null); setNotice(null); await qc.invalidateQueries({ queryKey: ["my-service-keys"] }); setCreated(k); },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeMyServiceKey(id),
    onSuccess: async () => {
      setRevoking(null);
      setNotice("Service key revoked. It can take up to five minutes to stop working everywhere.");
      await qc.invalidateQueries({ queryKey: ["my-service-keys"] });
    },
  });
  // The confirmations keep showing the key's name while they animate closed.
  const lastRotating = useRef<OwnedServiceKey | null>(null);
  const lastRevoking = useRef<OwnedServiceKey | null>(null);
  if (rotating) lastRotating.current = rotating;
  if (revoking) lastRevoking.current = revoking;
  const other = (k: OwnedServiceKey) => (k.role === "owner" ? k.backupOwner : k.owner);
  return (
    <>
      <h2 className="forge-typography--heading3 card-title">Service keys</h2>
      <p className="forge-typography--body2 muted card-title">
        Keys for shared applications that you own or back up. You can see their requests, and rotate or revoke them; an administrator changes their owners, models or budget.
      </p>
      {notice && (
        <forge-inline-message theme="success">
          <forge-icon slot="icon" name="check_circle" />
          <div slot="title">{notice}</div>
        </forge-inline-message>
      )}
      <forge-card>
        <div className="table-wrap">
          <table className="forge-data-table">
            <thead>
              <tr><th>Key</th><th>Your role</th><th>Labels</th><th>Models</th><th className="num">Spend (est.)</th><th>Last used</th><th aria-label="Actions" className="cell-sticky-end" /></tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id}>
                  <td><div>{shortAlias(k)}</div><div className="mono muted">…{k.id.slice(-8)}</div></td>
                  <td className="wrap-ok">
                    <forge-badge theme="info">{k.role === "owner" ? "Owner" : "Backup owner"}</forge-badge>
                    <div className="muted">{other(k) ? `${k.role === "owner" ? "Backup" : "Owner"}: ${other(k)}` : `No ${k.role === "owner" ? "backup owner" : "owner"} named`}</div>
                  </td>
                  <td><TagChips division={k.division} businessUnit={k.businessUnit} /></td>
                  <td className="wrap-ok cell-models" title={k.models.join(", ")}>{k.models.length ? (k.models.length > 2 ? `${k.models.slice(0, 2).join(", ")} +${k.models.length - 2}` : k.models.join(", ")) : <span className="muted">All models</span>}</td>
                  <td className="num">{money(k.spend)}</td>
                  <td className="nowrap">{when(k.lastActive)}</td>
                  <td className="nowrap cell-action cell-sticky-end">
                    <forge-icon-button aria-label={`Requests for ${shortAlias(k)}`} onClick={() => navigate(`/logs?key=${k.id}`)}>
                      <forge-icon name="receipt_long" />
                    </forge-icon-button>
                    <forge-tooltip>This key&apos;s requests (not what was sent)</forge-tooltip>
                    <forge-icon-button aria-label={`Rotate ${shortAlias(k)}`} disabled={me.readOnly || undefined} onClick={() => setRotating(k)}>
                      <forge-icon name="autorenew" />
                    </forge-icon-button>
                    <forge-tooltip>Replace with a new key</forge-tooltip>
                    <forge-icon-button aria-label={`Revoke ${shortAlias(k)}`} disabled={me.readOnly || undefined} onClick={() => setRevoking(k)}>
                      <forge-icon name="delete_outline" />
                    </forge-icon-button>
                    <forge-tooltip>Revoke</forge-tooltip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </forge-card>
      <Dialog open={!!rotating} onClose={() => setRotating(null)} label="Rotate service key">
        <h2 className="forge-typography--heading3 card-title">Rotate “{lastRotating.current && shortAlias(lastRotating.current)}”?</h2>
        <p className="forge-typography--body2">
          A new key replaces it, with the same owners, team, labels, models and budget. The current key stops working at once, so the
          application using it fails until the new key is installed.
        </p>
        {rotate.error && <ErrorMessage error={rotate.error} />}
        <div className="row gap-small">
          <forge-button variant="raised" disabled={rotate.isPending || undefined} onClick={() => rotating && rotate.mutate(rotating.id)}>Rotate</forge-button>
          <forge-button onClick={() => setRotating(null)}>Cancel</forge-button>
        </div>
      </Dialog>
      <Dialog open={!!revoking} onClose={() => setRevoking(null)} label="Revoke service key">
        <h2 className="forge-typography--heading3 card-title">Revoke “{lastRevoking.current && shortAlias(lastRevoking.current)}”?</h2>
        <p className="forge-typography--body2">
          The application using it stops working within about five minutes. This cannot be undone; an administrator can create a new key.
        </p>
        {revoke.error && <ErrorMessage error={revoke.error} />}
        <div className="row gap-small">
          <forge-button variant="raised" theme="error" disabled={revoke.isPending || undefined}
            onClick={() => revoking && revoke.mutate(revoking.id)}>Revoke</forge-button>
          <forge-button onClick={() => setRevoking(null)}>Cancel</forge-button>
        </div>
      </Dialog>
      <Dialog open={!!created} onClose={() => setCreated(null)} label="Your new service key" persistent>
        <h2 className="forge-typography--heading3 card-title">Copy the key now</h2>
        <forge-inline-message theme="warning">
          <forge-icon slot="icon" name="warning" />
          <div slot="title">This is the only time it is shown</div>
          <span>Put it in the application&apos;s secret store, never in chat or email. The old key has stopped working.</span>
        </forge-inline-message>
        {created && (
          <div className="secret-box">
            <code className="mono grow">{created.key}</code>
            <CopyButton text={created.key} />
          </div>
        )}
        <div><forge-button variant="raised" onClick={() => setCreated(null)}>Done</forge-button></div>
      </Dialog>
    </>
  );
}
